import { z } from "zod";

import {
	byPartyName,
	DIRECTORY_QUERY_MAX_LENGTH,
	directoryEntrySchema,
	PARTY_KIND_LABELS,
	PARTY_KINDS,
	type DirectoryEntry,
} from "./directory";

// Searching the Directory by meaning. Every directory row is kept as one document in an Amazon Bedrock knowledge base on
// an S3 Vectors index (`apps/backend/lib/api/directory/`), which finds the parties closest to a query however it is
// worded. The knowledge base is fed from the directory table alone, so it holds exactly what a row holds and nothing a
// row does not — never a person's private details.

/** The most related parties one search answers with. */
export const DIRECTORY_RELATED_LIMIT = 10;

/** The shortest search the Directory asks its knowledge base about: a shorter one is like everything, and means nothing. */
export const DIRECTORY_RELATED_MIN_LENGTH = 3;

/**
 * Whether a party's name holds what the search box holds, without regard to case — which is what the Directory's list
 * is narrowed by, and why a related party never is one: the list shows it already.
 */
export function nameHolds(party: Pick<DirectoryEntry, "name">, q: string): boolean {
	return party.name.toLowerCase().includes(q.trim().toLowerCase());
}

/** The query of `GET /api/directory/related`: what the search box holds, and the kind the pills show, if one. */
export const directoryRelatedQuerySchema = z.object({
	q: z.string().trim().min(DIRECTORY_RELATED_MIN_LENGTH).max(DIRECTORY_QUERY_MAX_LENGTH),
	kind: z.enum(PARTY_KINDS).optional(),
});
export type DirectoryRelatedQuery = z.infer<typeof directoryRelatedQuerySchema>;

/**
 * The parties related to a search, best first, as the Directory's list shows them after the ones whose name holds it:
 * none of them is one of those.
 */
export const directoryRelatedSchema = z.object({
	parties: z.array(directoryEntrySchema).max(DIRECTORY_RELATED_LIMIT),
});
export type DirectoryRelated = z.infer<typeof directoryRelatedSchema>;

/**
 * A party as the knowledge base reads it: the text Bedrock embeds for one directory row. It holds everything a person
 * or a document may find the party by, one fact to a line — its name, its kind and the line under its name, its legal
 * name, tax id, email and country — and nothing that changes from one day to the next, such as its standing.
 */
export function directoryDocumentText(entry: DirectoryEntry): string {
	return [
		entry.name,
		`${PARTY_KIND_LABELS[entry.kind]} · ${entry.detail}`,
		entry.legalName === undefined ? undefined : `Legal name: ${entry.legalName}`,
		entry.taxId === undefined ? undefined : `Tax ID: ${entry.taxId}`,
		entry.email === undefined ? undefined : `Email: ${entry.email}`,
		entry.country === undefined ? undefined : `Country: ${entry.country}`,
	]
		.filter(line => line !== undefined)
		.join("\n");
}

/** A text's words for the stand-in's search: without accents or punctuation, in lowercase, two letters or longer. */
function searchWords(text: string): string[] {
	return text
		.normalize("NFKD")
		.replaceAll(/\p{M}/gu, "")
		.toLowerCase()
		.split(/[^\p{L}\p{N}]+/u)
		.filter(word => word.length >= 2);
}

/**
 * The parties among `entries` related to `query`, best first, as the local API and the tests rank them in place of the
 * knowledge base, which they never call: a party is related when a word of its document begins with a word of the
 * query, and the more of the query's words it has, the better it ranks; parties that rank the same stay A→Z. It is a
 * stand-in, not a model of what the knowledge base answers — Bedrock also finds a party by meaning, where no word is
 * shared.
 */
export function rankRelatedParties(
	entries: readonly DirectoryEntry[],
	query: string,
	limit: number = DIRECTORY_RELATED_LIMIT,
): DirectoryEntry[] {
	const wanted = [...new Set(searchWords(query))];
	if (wanted.length === 0) {
		return [];
	}
	return byPartyName(entries)
		.map(entry => {
			const words = searchWords(directoryDocumentText(entry));
			return { entry, score: wanted.filter(word => words.some(found => found.startsWith(word))).length };
		})
		.filter(({ score }) => score > 0)
		.toSorted((a, b) => b.score - a.score)
		.slice(0, limit)
		.map(({ entry }) => entry);
}
